<template>
  <section class="panel">
    <div class="panel-header">
      <h2>Screen Snitch</h2>

      <div class="panel-header-right">
        <div class="filter-pills">
          <button
            v-for="option in filterOptions"
            :key="option.value"
            class="filter-pill"
            :class="{ 'filter-pill--active': imageFilter === option.value }"
            @click="imageFilter = option.value"
          >
            {{ option.label }}
            <span class="filter-pill__count">
              {{ getFilterCount(option.value) }}
            </span>
          </button>
        </div>

        <div class="panel-meta">
          <span>{{ filteredDevices.length }} devices</span>
          <span>Refresh {{ refreshSeconds }}s</span>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      ref="scrollContainer"
      @mouseenter="pauseAutoScroll"
      @mouseleave="resumeAutoScroll"
    >
      <div class="screen-grid">
        <div
          v-for="device in visibleDevices"
          :key="device"
          class="screen-tile"
          @click="showDevice(device)"
        >
          <div class="screen-label-row">
            <div class="screen-label">
              {{ device }}
            </div>

            <div
              v-if="capturePill(device)"
              class="capture-pill"
              :class="`capture-pill--${capturePill(device).tone}`"
            >
              {{ capturePill(device).text }}
            </div>
          </div>

          <img
            class="screen-image"
            :key="`${device}-${triggerRefresh}`"
            :src="
              `${snitchDomain}/captures/${device}-latest.jpg?${triggerRefresh}`
            "
            :alt="device"
            @error="markImageError(device)"
            @load="handleImageLoad(device)"
          />

          <div v-if="erroredImages[device]" class="screen-fallback">
            No screenshot
          </div>
        </div>
      </div>
    </div>

    <Transition name="fade">
      <DeviceModal
        v-if="deviceActive"
        :device="deviceActive"
        :trigger-refresh="triggerRefresh"
        @closeModal="deviceActive = ''"
      />
    </Transition>
  </section>
</template>

<script>
import axios from "axios";
import DeviceModal from "@/components/DeviceModal.vue";

export default {
  components: {
    DeviceModal,
  },

  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      deviceBlacklist: [
        "boardroom-1",
        "project-1",
        "waterford-touch-hd-1",
        "waterford-touch-hd-2",
        "waterford-touch-hd-3",
        "waterford-touch-uhd-1",
        "waterford-video-1",
        "larrys-macbook",
      ],
      snitchDomain: process.env.VUE_APP_SNITCH_SERVER,
      snitches: {},
      triggerRefresh: 0,
      refreshRate: 100000,
      deviceActive: "",
      erroredImages: {},
      refreshInterval: null,
      imageRefreshInterval: null,

      autoScrollInterval: null,
      autoScrollPaused: false,
      autoScrollStep: 1,
      autoScrollDelay: 20,

      imageFilter: "all",
      filterOptions: [
        { label: "All", value: "all" },
        { label: "Available", value: "available" },
        { label: "Missing", value: "missing" },
      ],

      captureMeta: {},
      captureNowTick: Date.now(),
      captureNowInterval: null,
    };
  },

  watch: {
    imageFilter() {
      this.$nextTick(() => {
        const el = this.$refs.scrollContainer;
        if (el) el.scrollTop = 0;
      });

      this.fetchVisibleCaptureMetadata();
    },

    visibleDevices: {
      immediate: false,
      handler() {
        this.fetchVisibleCaptureMetadata();
      },
    },
  },

  computed: {
    normalizedSearch() {
      return this.searchQuery.trim().toLowerCase();
    },

    refreshSeconds() {
      return Math.round(this.refreshRate / 1000);
    },

    sortedSnitches() {
      const normalizedSnitches = {};

      Object.keys(this.snitches).forEach(snitch => {
        const normalizedSnitch = snitch
          .toLowerCase()
          .replace(".local", "")
          .trim();

        normalizedSnitches[normalizedSnitch] =
          normalizedSnitches[normalizedSnitch] || {};

        Object.assign(
          normalizedSnitches[normalizedSnitch],
          this.snitches[snitch]
        );
      });

      return Object.keys(normalizedSnitches)
        .sort()
        .reduce((result, key) => {
          result[key] = normalizedSnitches[key];
          return result;
        }, {});
    },

    devices() {
      return Object.keys(this.sortedSnitches);
    },

    filteredDevices() {
      const nonBlacklistedDevices = this.devices.filter(
        device => !this.deviceBlacklist.includes(device)
      );

      if (!this.normalizedSearch) return nonBlacklistedDevices;

      return nonBlacklistedDevices.filter(device =>
        device.toLowerCase().includes(this.normalizedSearch)
      );
    },

    availableDevices() {
      return this.filteredDevices.filter(device => !this.erroredImages[device]);
    },

    missingDevices() {
      return this.filteredDevices.filter(
        device => !!this.erroredImages[device]
      );
    },

    visibleDevices() {
      if (this.imageFilter === "available") return this.availableDevices;
      if (this.imageFilter === "missing") return this.missingDevices;
      return this.filteredDevices;
    },
  },

  mounted() {
    this.getSnitches();

    this.refreshInterval = setInterval(() => {
      this.getSnitches();
    }, 5000);

    this.imageRefreshInterval = setInterval(() => {
      this.triggerRefresh++;
      this.fetchVisibleCaptureMetadata();
    }, this.refreshRate);

    this.captureNowInterval = setInterval(() => {
      this.captureNowTick = Date.now();
    }, 30000);

    this.startAutoScroll();
  },

  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.imageRefreshInterval) clearInterval(this.imageRefreshInterval);
    if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);
    if (this.captureNowInterval) clearInterval(this.captureNowInterval);
  },

  methods: {
    getSnitches() {
      axios
        .get(
          process.env.VUE_APP_SNITCH_SERVER
            ? `${process.env.VUE_APP_SNITCH_SERVER}/api/snitches`
            : "/api/snitches"
        )
        .then(res => {
          this.snitches = res.data;
          this.$nextTick(() => {
            this.fetchVisibleCaptureMetadata();
          });
        });
    },

    showDevice(device) {
      this.deviceActive = device;
    },

    markImageError(device) {
      this.$set(this.erroredImages, device, true);
      this.$delete(this.captureMeta, device);
    },

    clearImageError(device) {
      this.$set(this.erroredImages, device, false);
    },

    handleImageLoad(device) {
      this.clearImageError(device);
      this.fetchCaptureMetadata(device);
    },

    getFilterCount(filter) {
      if (filter === "available") return this.availableDevices.length;
      if (filter === "missing") return this.missingDevices.length;
      return this.filteredDevices.length;
    },

    startAutoScroll() {
      if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);

      this.autoScrollInterval = setInterval(() => {
        if (this.autoScrollPaused) return;

        const el = this.$refs.scrollContainer;
        if (!el) return;

        const bottom = el.scrollHeight - el.clientHeight;
        if (bottom <= 0) return;

        const nextScrollTop = el.scrollTop + this.autoScrollStep;
        const threshold = 4;

        if (nextScrollTop >= bottom - threshold) {
          el.scrollTop = 0;
        } else {
          el.scrollTop = nextScrollTop;
        }
      }, this.autoScrollDelay);
    },

    pauseAutoScroll() {
      this.autoScrollPaused = true;
    },

    resumeAutoScroll() {
      this.autoScrollPaused = false;
    },

    async fetchCaptureMetadata(device) {
      if (!device || this.erroredImages[device]) return;

      try {
        const imageUrl = `${this.snitchDomain}/captures/${device}-latest.jpg`;

        const response = await axios.head(imageUrl, {
          params: {
            t: this.triggerRefresh,
          },
        });

        const lastModified = response.headers["last-modified"];
        if (!lastModified) return;

        const parsed = new Date(lastModified);
        if (Number.isNaN(parsed.getTime())) return;

        this.$set(this.captureMeta, device, {
          lastModified,
          timestamp: parsed.getTime(),
        });
      } catch (error) {
        this.$delete(this.captureMeta, device);
      }
    },

    fetchVisibleCaptureMetadata() {
      this.visibleDevices.forEach(device => {
        if (!this.erroredImages[device]) {
          this.fetchCaptureMetadata(device);
        }
      });
    },

    capturePill(device) {
      const meta = this.captureMeta[device];
      if (!meta || !meta.timestamp) return null;

      const diffMs = this.captureNowTick - meta.timestamp;
      const minutes = Math.max(0, Math.floor(diffMs / 1000 / 60));

      let tone = "ok";

      if (minutes >= 10) {
        tone = "bad";
      } else if (minutes >= 5) {
        tone = "warn";
      }

      let text;

      if (minutes < 1) {
        text = "< 1 min";
      } else if (minutes < 60) {
        text = `${minutes} min`;
      } else if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        text = `${hours}h ${mins}m`;
      } else {
        const days = Math.floor(minutes / 1440);
        const hours = Math.floor((minutes % 1440) / 60);
        text = `${days}d ${hours}h`;
      }

      return {
        text,
        tone,
      };
    },
  },
};
</script>

<style scoped>
.panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  background: #0b1622;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.panel-header {
  flex-shrink: 0;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #f1f5f9;
}

.panel-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.filter-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s ease;
}

.filter-pill:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #f8fafc;
}

.filter-pill--active {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(96, 165, 250, 0.45);
  color: #eff6ff;
}

.filter-pill__count {
  color: #94a3b8;
}

.filter-pill--active .filter-pill__count {
  color: #dbeafe;
}

.panel-meta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  color: #94a3b8;
  font-size: 0.82rem;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

.panel-body::-webkit-scrollbar {
  width: 8px;
}

.panel-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

.screen-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.screen-tile {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  background: #08131f;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.screen-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.screen-label-row {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  pointer-events: none;
}

.screen-label {
  max-width: calc(100% - 88px);
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(8, 19, 31, 0.8);
  color: #f8fafc;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.capture-pill {
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.capture-pill--ok {
  color: #86efac;
}

.capture-pill--warn {
  color: #fcd34d;
}

.capture-pill--bad {
  color: #fca5a5;
}

.screen-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 19, 31, 0.88);
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 500;
}

.fade-leave-active,
.fade-enter-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
